import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { getCsrfToken, getProdJobsUrl } from "../../utils/apiHelper";
import "../../styles/theme.css";

const FREQUENCY_OPTIONS = [
  { value: "Daily", labelKey: "frequencies.daily" },
  { value: "Weekly", labelKey: "frequencies.weekly" },
  { value: "Monthly", labelKey: "frequencies.monthly" },
  { value: "Single Run", labelKey: "frequencies.singleRun" },
];

const DAY_OPTIONS = [
  { value: "Monday", labelKey: "days.monday", abbr: "mon" },
  { value: "Tuesday", labelKey: "days.tuesday", abbr: "tue" },
  { value: "Wednesday", labelKey: "days.wednesday", abbr: "wed" },
  { value: "Thursday", labelKey: "days.thursday", abbr: "thu" },
  { value: "Friday", labelKey: "days.friday", abbr: "fri" },
  { value: "Saturday", labelKey: "days.saturday", abbr: "sat" },
  { value: "Sunday", labelKey: "days.sunday", abbr: "sun" },
];

const dayNameToAbbr = Object.fromEntries(DAY_OPTIONS.map((day) => [day.value, day.abbr]));

function getDefaultRunAt() {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatHourLabel(hour, t) {
  if (hour === 0) return t("timeAm", { hour: 12 });
  if (hour < 12) return t("timeAm", { hour });
  if (hour === 12) return t("timePm", { hour: 12 });
  return t("timePm", { hour: hour - 12 });
}

function ScheduleTestModal({ isOpen, onClose, selectedProfileId, onScheduleCreated }) {
  const { t } = useTranslation("scheduleTestModal");
  const [frequency, setFrequency] = useState("Daily");
  const [timeOfDay, setTimeOfDay] = useState("00:00");
  const [runAt, setRunAt] = useState(getDefaultRunAt);
  const [dayOfWeek, setDayOfWeek] = useState("Monday");
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFrequency("Daily");
      setTimeOfDay("00:00");
      setRunAt(getDefaultRunAt());
      setDayOfWeek("Monday");
      setDayOfMonth(1);
      setError(null);
      setLoading(false);
    }
  }, [isOpen]);

  const handleSchedule = async () => {
    if (!selectedProfileId) {
      setError(t("errors.selectProfile"));
      return;
    }

    const isSingleRun = frequency === "Single Run";

    if (isSingleRun) {
      const runAtDate = new Date(runAt);
      if (!runAt || Number.isNaN(runAtDate.getTime())) {
        setError(t("errors.invalidDateTime"));
        return;
      }
      if (runAtDate.getTime() <= Date.now()) {
        setError(t("errors.runAtFuture"));
        return;
      }
    } else {
      // Validate time format
      if (!/^([0-1][0-9]|2[0-3]):00$/.test(timeOfDay)) {
        setError(t("errors.invalidTime"));
        return;
      }
    }

    // Validate day of month
    if (frequency === "Monthly" && (dayOfMonth < 1 || dayOfMonth > 31)) {
      setError(t("errors.invalidDayOfMonth"));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get CSRF token for session-based authentication
      const csrfToken = await getCsrfToken();

      // Build schedule data
      const scheduleData = {
        profile_id: selectedProfileId,
        frequency: isSingleRun ? "single_run" : frequency.toLowerCase(),
      };

      if (isSingleRun) {
        scheduleData.run_at = new Date(runAt).toISOString();
      } else {
        scheduleData.time_of_day = timeOfDay; // API accepts HH:MM or HH:MM:SS
        // Add day selection based on frequency
        if (frequency === "Weekly") {
          scheduleData.day_of_week = dayNameToAbbr[dayOfWeek] || dayOfWeek.toLowerCase().substring(0, 3);
        } else if (frequency === "Monthly") {
          scheduleData.day_of_month = dayOfMonth;
        }
      }

      // Create FormData for the request
      const formData = new FormData();
      formData.append('profile_id', scheduleData.profile_id);
      formData.append('frequency', scheduleData.frequency);
      if (scheduleData.time_of_day) {
        formData.append('time_of_day', scheduleData.time_of_day);
      }
      if (scheduleData.run_at) {
        formData.append('run_at', scheduleData.run_at);
      }
      if (scheduleData.day_of_week) {
        formData.append('day_of_week', scheduleData.day_of_week);
      }
      if (scheduleData.day_of_month) {
        formData.append('day_of_month', scheduleData.day_of_month);
      }

      const response = await fetch(getProdJobsUrl('schedule/'), {
        method: 'POST',
        credentials: 'include', // Send session cookies
        headers: {
          'X-CSRFToken': csrfToken
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Schedule created successfully:', result);

      // Check if there's an error in the response
      if (result.error) {
        throw new Error(result.message || t("errors.scheduleFailed"));
      }

      alert(t("success"));
      // Close modal and notify parent
      onClose();
      if (onScheduleCreated) {
        onScheduleCreated(result);
      }
    } catch (error) {
      console.error('Error scheduling test:', error);
      setError(error.message || t("errors.scheduleFailed"));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Determine if dark theme is active
  const isDarkTheme = document.documentElement.getAttribute('data-theme') === 'dark';
  const borderColor = isDarkTheme ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.15)';

  return (
    <div 
      className="modal-backdrop" 
      style={{
        zIndex: 9999
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="modal-content"
        style={{
          animation: "fadeIn 0.3s ease-out",
          maxWidth: "500px"
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "15px",
            right: "15px",
            background: "transparent",
            border: "none",
            fontSize: "24px",
            cursor: "pointer",
            color: "var(--card-text)",
            zIndex: 10000
          }}
        >
          <X size={24} />
        </button>

        <h3>{t("title")}</h3>
        
        {error && (
          <div style={{
            padding: "12px 16px",
            backgroundColor: "#fee",
            color: "#c33",
            borderRadius: "8px",
            marginBottom: "20px",
            fontSize: "14px"
          }}>
            {error}
          </div>
        )}
        
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {/* Frequency and Time of Day Row */}
          <div style={{ display: "flex", gap: "16px" }}>
            {/* Frequency Selection */}
            <div style={{ flex: 1 }}>
              <label style={{ 
                display: "block", 
                marginBottom: "8px", 
                color: "var(--card-text)",
                fontSize: "14px",
                fontWeight: "500"
              }}>
                {t("frequency")} <span style={{ color: "#d32f2f" }}>*</span>
              </label>
              <select
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  borderRadius: "8px",
                  border: `2px solid ${borderColor}`,
                  backgroundColor: "var(--card-bg)",
                  color: "var(--card-text)",
                  fontSize: "14px",
                  outline: "none",
                  boxSizing: "border-box",
                  cursor: "pointer",
                  transition: "border-color 0.2s ease, box-shadow 0.2s ease"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "var(--btn-gradient-start)";
                  e.target.style.boxShadow = "0 0 0 3px rgba(82, 106, 231, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = borderColor;
                  e.target.style.boxShadow = "none";
                }}
              >
                {FREQUENCY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </option>
                ))}
              </select>
            </div>

            {/* Time of Day */}
            {frequency !== "Single Run" && (
            <div style={{ flex: 1 }}>
              <label style={{ 
                display: "block", 
                marginBottom: "8px", 
                color: "var(--card-text)",
                fontSize: "14px",
                fontWeight: "500"
              }}>
                {t("timeOfDay")} <span style={{ color: "#d32f2f" }}>*</span>
              </label>
              <select
                value={timeOfDay}
                onChange={(e) => setTimeOfDay(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  borderRadius: "8px",
                  border: `2px solid ${borderColor}`,
                  backgroundColor: "var(--card-bg)",
                  color: "var(--card-text)",
                  fontSize: "14px",
                  outline: "none",
                  boxSizing: "border-box",
                  cursor: "pointer",
                  transition: "border-color 0.2s ease, box-shadow 0.2s ease"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "var(--btn-gradient-start)";
                  e.target.style.boxShadow = "0 0 0 3px rgba(82, 106, 231, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = borderColor;
                  e.target.style.boxShadow = "none";
                }}
              >
                {Array.from({ length: 24 }, (_, i) => {
                  const h = i.toString().padStart(2, "0");
                  const val = `${h}:00`;
                  return (
                    <option key={val} value={val}>
                      {formatHourLabel(i, t)}
                    </option>
                  );
                })}
              </select>
            </div>
            )}
          </div>

          {/* Run At - Single Run */}
          {frequency === "Single Run" && (
            <div>
              <label style={{ 
                display: "block", 
                marginBottom: "8px", 
                color: "var(--card-text)",
                fontSize: "14px",
                fontWeight: "500"
              }}>
                {t("runAt")} <span style={{ color: "#d32f2f" }}>*</span>
              </label>
              <input
                type="datetime-local"
                value={runAt}
                onChange={(e) => setRunAt(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  borderRadius: "8px",
                  border: `2px solid ${borderColor}`,
                  backgroundColor: "var(--card-bg)",
                  color: "var(--card-text)",
                  fontSize: "14px",
                  outline: "none",
                  boxSizing: "border-box",
                  cursor: "pointer",
                  transition: "border-color 0.2s ease, box-shadow 0.2s ease"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "var(--btn-gradient-start)";
                  e.target.style.boxShadow = "0 0 0 3px rgba(82, 106, 231, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = borderColor;
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>
          )}

          {/* Day Selection - Weekly */}
          {frequency === "Weekly" && (
            <div>
              <label style={{ 
                display: "block", 
                marginBottom: "8px", 
                color: "var(--card-text)",
                fontSize: "14px",
                fontWeight: "500"
              }}>
                {t("dayOfWeek")} <span style={{ color: "#d32f2f" }}>*</span>
              </label>
              <select
                value={dayOfWeek}
                onChange={(e) => setDayOfWeek(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  borderRadius: "8px",
                  border: `2px solid ${borderColor}`,
                  backgroundColor: "var(--card-bg)",
                  color: "var(--card-text)",
                  fontSize: "14px",
                  outline: "none",
                  boxSizing: "border-box",
                  cursor: "pointer",
                  transition: "border-color 0.2s ease, box-shadow 0.2s ease"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "var(--btn-gradient-start)";
                  e.target.style.boxShadow = "0 0 0 3px rgba(82, 106, 231, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = borderColor;
                  e.target.style.boxShadow = "none";
                }}
              >
                {DAY_OPTIONS.map((day) => (
                  <option key={day.value} value={day.value}>
                    {t(day.labelKey)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Day Selection - Monthly */}
          {frequency === "Monthly" && (
            <div>
              <label style={{ 
                display: "block", 
                marginBottom: "8px", 
                color: "var(--card-text)",
                fontSize: "14px",
                fontWeight: "500"
              }}>
                {t("dayOfMonth")} <span style={{ color: "#d32f2f" }}>*</span>
              </label>
              <select
                value={dayOfMonth}
                onChange={(e) => setDayOfMonth(parseInt(e.target.value, 10))}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  borderRadius: "8px",
                  border: `2px solid ${borderColor}`,
                  backgroundColor: "var(--card-bg)",
                  color: "var(--card-text)",
                  fontSize: "14px",
                  outline: "none",
                  boxSizing: "border-box",
                  cursor: "pointer",
                  transition: "border-color 0.2s ease, box-shadow 0.2s ease"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "var(--btn-gradient-start)";
                  e.target.style.boxShadow = "0 0 0 3px rgba(82, 106, 231, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = borderColor;
                  e.target.style.boxShadow = "none";
                }}
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                  <option key={day} value={day}>
                    {day}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end", marginTop: "24px" }}>
          <button 
            className="btn btn-secondary" 
            onClick={onClose}
            disabled={loading}
          >
            {t("cancel")}
          </button>
          <button 
            className="btn btn-primary" 
            onClick={handleSchedule}
            disabled={!selectedProfileId || loading}
          >
            {loading ? t("scheduling") : t("scheduleTest")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ScheduleTestModal;
